import { create, useModal } from '@ebay/nice-modal-react';
import Button from '@shared/components/Button/Button';
import Popup from '@shared/components/Popup/Popup';
import cx from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Check } from 'assets/icons/Check.svg';
import { ReactComponent as DeviceUnverified } from 'assets/icons/DeviceUnverified.svg';
import { ReactComponent as X } from 'assets/icons/X.svg';
import GradientIconWithShadow from 'components/GradientIconWithShadow/GradientIconWithShadow';
import {
  completeLoginSecretStorageUnlock,
  getLoginSecretStorageUnlockPhase,
  isLoginSecretStorageUnlockActive,
  isLoginUnlockBrokenIdentity,
  markLoginUnlockDeferredResetNeedsFullFlow,
  markLoginUnlockDeferredResetPending,
  markLoginUnlockResetCompleted,
} from 'contexts/MatrixContext/loginSecretStorageUnlock';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useMatrixDeviceContext from 'contexts/MatrixDeviceContext/useMatrixDeviceContext';
import useResetKeys from 'contexts/MatrixDeviceVerificationContext/ResetKeys/useResetKeys';
import useToast from 'contexts/ToastProvider/useToast';
import { SessionStateKeys, useSessionState } from 'hooks/useSessionState';
import { clearMessengerUnreadAfterEncryptionReset } from 'modules/Messenger/utils/clearMessengerUnreadAfterEncryptionReset';
import logger from 'utils/logging/faro';
import testingConstants from 'utils/testingConstants';
import { IconType } from 'utils/types';

// idle           → before the first click
// starting       → reset running, reaching the auth challenge / opening MAS
// awaiting_continue → paused on the user authorising in MAS (Continue is actionable)
// finishing      → completing the upload after the user pressed Continue
type ResetFlowState = 'idle' | 'starting' | 'awaiting_continue' | 'finishing';

// Bounds the phases where the SDK is actively working (reaching the auth callback, finishing the
// upload). The MAS authorisation wait is intentionally excluded — the user may take minutes there.
// On poisoned accounts resetEncryption can stall on /keys/query, so this stops the spinner and
// surfaces an error instead of hanging forever.
const RESET_KEYS_TIMEOUT_MS = 30_000;

interface ConsequenceRowProps {
  icon: IconType;
  iconClassName: string;
  label: string;
}

const ConsequenceRow = ({ icon: Icon, iconClassName, label }: ConsequenceRowProps) => (
  <div className="flex items-start gap-2">
    <Icon className={cx('mt-0.5 h-5 w-5 shrink-0 stroke-2', iconClassName)} />
    <span className="text-sm text-black">{label}</span>
  </div>
);

interface ResetKeysModalProps {
  /** When true, reset runs during login unlock — completion resolves the login gate. */
  isLoginUnlock?: boolean;
  /** Invoked on "Not now"/close to return to the previous step (e.g. method picker). */
  onBack?: () => void;
}

const ResetKeysModal = create<ResetKeysModalProps>(({ isLoginUnlock: isLoginUnlockProp, onBack }) => {
  const { t } = useTranslation();
  const modal = useModal();
  const { resetKeys, resumeReset, openCrossSigningResetInMas } = useResetKeys();
  const { matrixClient: contextClient, matrixClientRef } = useMatrixContext({ requireDefinedMatrixClient: false });
  const { refreshCurrentDeviceStatus, refreshHasSecurityCode, checkForVerifiedDevices } = useMatrixDeviceContext();
  const { pushToast } = useToast();
  const [, setNeedsSecurityCodePromptAfterBootstrap] = useSessionState(
    SessionStateKeys.NEEDS_SECURITY_CODE_PROMPT_AFTER_BOOTSTRAP,
    false
  );
  const [flowState, setFlowState] = useState<ResetFlowState>('idle');

  // The tab opened synchronously on click (Safari popup-blocker workaround); the reset callback
  // navigates it to MAS once it has the account-management URL.
  const preOpenedWindowRef = useRef<Window | null>(null);
  // Which external authorisation the paused reset is waiting on (set when auth callback suspends).
  const authorizationTypeRef = useRef<'account' | 'sso' | null>(null);
  // True once resetEncryption has suspended at the auth callback and resumeReset can work.
  const resetSuspendedRef = useRef(false);
  // User clicked Continue before the reset suspended — auto-resume once suspension arrives.
  const continuePendingRef = useRef(false);
  // Poisoned account at the pre_sync login gate: resetEncryption would hang before startClient, so
  // the reset is deferred — Continue resolves the gate and useStartMatrixClient runs the reset with
  // a started client (BUG-013 Fix C). Decided at first click, when the gate phase is still pre_sync.
  const deferredResetRef = useRef(false);
  // Guards against the timeout and the resolved reset both finishing the flow.
  const settledRef = useRef(false);

  const isLoginUnlock = isLoginUnlockProp ?? isLoginSecretStorageUnlockActive();
  const matrixClient = contextClient ?? (isLoginUnlock ? matrixClientRef.current : null);

  const handleDismiss = useCallback(() => {
    modal.remove();
    onBack?.();
  }, [modal, onBack]);

  useEffect(() => {
    logger.info('Reset keys flow opened');
  }, []);

  const failFlow = useCallback(() => {
    preOpenedWindowRef.current?.close();
    continuePendingRef.current = false;
    resetSuspendedRef.current = false;
    authorizationTypeRef.current = null;
    pushToast({ title: t('verification.reset_keys_failed'), variant: 'error' });
    setFlowState('idle');
  }, [pushToast, t]);

  const tryResumeReset = useCallback(() => {
    if (!resetSuspendedRef.current) return false;
    resumeReset();
    return true;
  }, [resumeReset]);

  const handleResetSuccess = useCallback(async () => {
    logger.info('Reset keys success');
    // During login unlock the client has no synced messenger rooms yet, so skip the cleanup.
    if (matrixClient) await clearMessengerUnreadAfterEncryptionReset(matrixClient);
    await refreshCurrentDeviceStatus();
    await refreshHasSecurityCode();
    await checkForVerifiedDevices();

    modal.remove();

    // Persist create-code intent for BootstrapSecurityCodeGate (app root). Showing the modal here
    // would flash and disappear — login-gate resolution tears down modals mounted in this subtree.
    setNeedsSecurityCodePromptAfterBootstrap(true);
    logger.info('Reset keys security code prompt scheduled');

    if (isLoginUnlock) {
      markLoginUnlockResetCompleted();
      completeLoginSecretStorageUnlock();
    }
  }, [
    matrixClient,
    refreshCurrentDeviceStatus,
    refreshHasSecurityCode,
    checkForVerifiedDevices,
    modal,
    setNeedsSecurityCodePromptAfterBootstrap,
    isLoginUnlock,
  ]);

  const runReset = useCallback(async () => {
    const result = await resetKeys({
      preOpenedWindow: preOpenedWindowRef.current,
      onAuthorizationRequired: (type) => {
        authorizationTypeRef.current = type;
        resetSuspendedRef.current = true;
        setFlowState((state) => (state === 'starting' ? 'awaiting_continue' : state));
        if (continuePendingRef.current) {
          continuePendingRef.current = false;
          setFlowState('finishing');
          tryResumeReset();
        }
      },
    });

    // A phase timeout already finished the flow — ignore the late resolution.
    if (settledRef.current) return;

    // SSO fallback completes out of band; wait for the user to come back and press Continue.
    if (result === 'auth_redirect') {
      setFlowState('awaiting_continue');
      return;
    }

    settledRef.current = true;

    if (result === 'ok') {
      await handleResetSuccess();
      return;
    }

    logger.error('Reset keys failed');
    failFlow();
  }, [resetKeys, handleResetSuccess, failFlow, tryResumeReset]);

  const handlePrimaryAction = useCallback(async () => {
    // Continue: resume the paused reset (MAS) or re-run it for the SSO fallback.
    if (flowState === 'awaiting_continue') {
      logger.info('Reset keys continue clicked');

      // pre_sync account (poisoned or healthy): do NOT run resetEncryption here — it hangs before
      // the client is started. Resolve the gate so startClient runs; useStartMatrixClient then
      // handles the reset with a started client (BUG-013 Fix C, BUG-015).
      if (deferredResetRef.current) {
        logger.info('Reset keys: deferring reset until client started (pre_sync account)');
        settledRef.current = true;
        preOpenedWindowRef.current?.close();
        markLoginUnlockResetCompleted();
        markLoginUnlockDeferredResetPending();
        modal.remove();
        completeLoginSecretStorageUnlock();
        return;
      }

      if (authorizationTypeRef.current === 'sso') {
        settledRef.current = false;
        setFlowState('finishing');
        await runReset();
        return;
      }
      setFlowState('finishing');
      if (!tryResumeReset()) {
        // resetEncryption has not reached the auth callback yet — queue resume for when it does.
        continuePendingRef.current = true;
      }
      return;
    }

    if (flowState !== 'idle') return;

    logger.info('Reset keys clicked');
    settledRef.current = false;
    continuePendingRef.current = false;
    resetSuspendedRef.current = false;
    authorizationTypeRef.current = null;
    deferredResetRef.current = false;
    // Open the tab synchronously while we still have the user gesture (Safari).
    preOpenedWindowRef.current = window.open('', '_blank');
    setFlowState('starting');

    // Navigate MAS immediately — do not wait for resetEncryption to reach the auth callback.
    const masOpened = await openCrossSigningResetInMas(preOpenedWindowRef.current);
    if (masOpened) {
      setFlowState('awaiting_continue');
    }

    // Running resetEncryption before startClient hangs on ALL accounts — the SDK writes
    // account_data successfully but stalls before device_signing/upload (Faro HchDv0aF8q for
    // poisoned, Faro b1XUCAraRw for healthy). Defer for every pre_sync reset. Only when MAS
    // opened — a non-MAS server has no authorisation deep-link (BUG-013 Fix C, BUG-015).
    //
    // Poisoned accounts: masAlreadyAuthorised works on the .eu non-UIA 401 server — the deferred
    // reset auto-runs in useStartMatrixClient.
    // Healthy accounts: standard-UIA requires a fresh session-scoped MAS authorisation that was
    // not obtained at click-time (no session param in the URL). PostLoginResetGate re-opens this
    // modal after login so the user can complete a proper UIA flow with a started client.
    if (masOpened && isLoginUnlock && getLoginSecretStorageUnlockPhase() === 'pre_sync') {
      deferredResetRef.current = true;
      if (!isLoginUnlockBrokenIdentity()) {
        // Healthy account: signal that the deferred reset needs the full UIA flow post-login.
        markLoginUnlockDeferredResetNeedsFullFlow();
      }
      return;
    }

    await runReset();
  }, [flowState, isLoginUnlock, tryResumeReset, runReset, openCrossSigningResetInMas, modal]);

  // Stop the spinner if the SDK never settles while it is meant to be working (see timeout note).
  useEffect(() => {
    if (flowState !== 'starting' && flowState !== 'finishing') return undefined;

    const timeoutId = setTimeout(() => {
      if (settledRef.current) return;
      settledRef.current = true;
      logger.error('Reset keys timed out', { phase: flowState });
      failFlow();
    }, RESET_KEYS_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [flowState, failFlow]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Enter') return;
      if (flowState === 'starting' || flowState === 'finishing') return;
      const activeEl = document.activeElement;
      if (activeEl !== document.body && activeEl !== null) return;
      event.preventDefault();
      handlePrimaryAction();
    },
    [flowState, handlePrimaryAction]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const isPrimaryLoading = flowState === 'starting' || flowState === 'finishing';
  const isPrimaryButtonContinue = flowState !== 'idle';

  return (
    <Popup isVisible isViewportScrollable closePopup={handleDismiss}>
      <div
        data-testid={testingConstants.resetKeysModal}
        className="relative flex w-full flex-col items-center rounded-2xl border border-gray-30 bg-gray-10 px-6 pb-6 pt-12 md:w-[512px]"
      >
        <button
          type="button"
          data-testid={testingConstants.closeModalButton}
          onClick={handleDismiss}
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-xl transition duration-150 ease-in-out hover:bg-gray-30"
        >
          <X className="h-5 w-5 stroke-black stroke-2" />
        </button>

        <GradientIconWithShadow
          icon={DeviceUnverified}
          bgVariant="lightRed"
          iconVariant="alert"
          iconClassName="stroke-1.5"
          className="backdrop-blur-[20px]"
        />

        <div className="mt-6 flex flex-col items-center gap-2 text-center">
          <h2 className="text-xl text-black">
            {t('verification.reset_cant_verify_device')}
            <br />
            {t('verification.reset_here_is_another_way')}
          </h2>
          <p className="text-base text-black">{t('verification.reset_account_no_access_body')}</p>
        </div>

        <div
          data-testid={testingConstants.resetConsequencesList}
          className="mt-6 w-full rounded-2xl border border-gray-30 bg-white p-4"
        >
          <p className="mb-2 text-base font-medium text-black">{t('verification.reset_account_you_can_reset')}</p>
          <div className="flex flex-col gap-1">
            <ConsequenceRow
              icon={Check}
              iconClassName="stroke-success-50"
              label={t('verification.reset_account_consequence_start_fresh')}
            />
            <ConsequenceRow
              icon={Check}
              iconClassName="stroke-success-50"
              label={t('verification.reset_account_consequence_new_security_code')}
            />
            <ConsequenceRow
              icon={X}
              iconClassName="stroke-tertiary-50"
              label={t('verification.reset_account_consequence_lose_history')}
            />
          </div>
        </div>

        <div className="mt-6 flex w-full items-center justify-center gap-2">
          <Button
            data-testid={testingConstants.resetNotNowBtn}
            variant="secondaryTertiary"
            onClick={handleDismiss}
            className="h-12 rounded-2xl border-gray-40 px-6"
          >
            {t('verification.reset_not_now')}
          </Button>
          {isPrimaryButtonContinue ? (
            <Button
              data-testid={testingConstants.resetContinueBtn}
              onClick={handlePrimaryAction}
              loading={isPrimaryLoading}
              className="h-12 rounded-2xl bg-tertiary-50 px-6 text-white hover:bg-tertiary-60"
            >
              {t('continue')}
            </Button>
          ) : (
            <Button
              data-testid={testingConstants.resetAccountBtn}
              onClick={handlePrimaryAction}
              loading={isPrimaryLoading}
              className="h-12 rounded-2xl bg-tertiary-50 px-6 text-white hover:bg-tertiary-60"
            >
              {t('verification.reset_account')}
            </Button>
          )}
        </div>
      </div>
    </Popup>
  );
});

export default ResetKeysModal;
